/**
 * PURPOSE: Annotation toolbar with tool selection, color picker, undo/redo
 */
import { useState } from 'react'
import { Undo2, Redo2, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { AnnotationTool } from './types'
import { TOOLS, COLORS } from './types'

interface ToolbarProps {
  activeTool: AnnotationTool; onToolChange: (tool: AnnotationTool) => void
  activeColor: string; onColorChange: (color: string) => void
  canUndo: boolean; canRedo: boolean; canClear: boolean
  onUndo: () => void; onRedo: () => void; onClear: () => void
}

export function Toolbar({ activeTool, onToolChange, activeColor, onColorChange, canUndo, canRedo, canClear, onUndo, onRedo, onClear }: ToolbarProps) {
  const [showColorPicker, setShowColorPicker] = useState(false)
  return (
    <footer className="flex-shrink-0 bg-black/90 px-3 py-3">
      <div className="flex items-center justify-between max-w-lg mx-auto">
        <div className="flex items-center gap-1">
          {TOOLS.map(({ tool, icon: Icon, label }) => (
            <button key={tool} onClick={() => onToolChange(tool)} className={cn('p-2.5 rounded-lg transition-colors', activeTool === tool ? 'bg-white text-black' : 'bg-white/10 text-white hover:bg-white/20')} title={label}><Icon className="w-5 h-5" /></button>
          ))}
        </div>
        <div className="relative">
          <button onClick={() => setShowColorPicker(!showColorPicker)} className="p-2.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" title="Color">
            <div className="w-5 h-5 rounded-full border-2 border-white" style={{ backgroundColor: activeColor }} />
          </button>
          {showColorPicker && (
            <div className="absolute bottom-full mb-2 right-0 bg-card border border-border rounded-xl p-2 shadow-xl flex gap-1.5">
              {COLORS.map(({ value, label }) => (
                <button key={value} onClick={() => { onColorChange(value); setShowColorPicker(false) }}
                  className={cn('w-8 h-8 rounded-full border-2 transition-transform', activeColor === value ? 'border-primary scale-110' : 'border-transparent hover:scale-105', value === '#ffffff' && 'border-gray-300')}
                  style={{ backgroundColor: value }} title={label} />
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onUndo} disabled={!canUndo} className="p-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-30" title="Undo"><Undo2 className="w-5 h-5" /></button>
          <button onClick={onRedo} disabled={!canRedo} className="p-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-30" title="Redo"><Redo2 className="w-5 h-5" /></button>
          <button onClick={onClear} disabled={!canClear} className="p-2.5 rounded-lg bg-white/10 text-red-400 hover:bg-white/20 transition-colors disabled:opacity-30" title="Clear all"><Trash2 className="w-5 h-5" /></button>
        </div>
      </div>
    </footer>
  )
}
